import React, { useCallback, useState } from "react";

import copy from "copy-to-clipboard";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { PermissionError } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/error_details_pb";

import { EventTypeReason } from "../constants";
import { GrpcError, SnackbarContext } from "./snackbar-context";
import { gRPCStatus } from "api/grpc-status-codes";
import CopyToClipboardToast from "components/shared/copy-to-clipboard-toast";
import DisplayErrorDialog from "components/shared/display-error-dialog";
import DisplayToastError from "components/shared/display-toast-error";

interface SnackbarContextWrapperProps {
  children: React.ReactNode;
}
const SnackbarContextWrapper = ({ children }: SnackbarContextWrapperProps) => {
  const [openClipboardSnackBar, setOpenClipboardSnackBar] = useState(false);
  const [toastErrorState, setToastErrorState] = useState({
    message: "",
    open: false,
  });
  const [dialogErrorOpen, setDialogErrorOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionType>(
    ActionType.UNSPECIFIED,
  );

  const copyToClipboard = useCallback((text: string) => {
    copy(text);
    setOpenClipboardSnackBar(true);
  }, []);

  const handleClipboardSnackbarClose = () => {
    setOpenClipboardSnackBar(false);
  };

  interface ExtendedGrpcError extends GrpcError {
    details: Array<{
      type: string;
      value: Uint8Array;
    }>;
  }

  const showError = useCallback((e: GrpcError) => {
    if (e.code === gRPCStatus.PERMISSION_DENIED) {
      const extendedError = e as ExtendedGrpcError;
      if (extendedError.details && extendedError.details.length > 0) {
        try {
          const permissionError = PermissionError.fromBinary(
            extendedError.details[0].value,
          );
          setActionType(permissionError.actionType);
        } catch {
          setActionType(ActionType.UNSPECIFIED);
        }
      } else {
        setActionType(ActionType.UNSPECIFIED);
      }

      setDialogErrorOpen(true);
    } else {
      setToastErrorState({
        message: e.message,
        open: true,
      });
    }
  }, []);

  const handleErrorAlertClose = (
    event?: Event | React.SyntheticEvent<unknown, Event>,
    reason?: string,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setToastErrorState({
      message: "",
      open: false,
    });
  };

  const handleDialogClose = () => {
    setDialogErrorOpen(false);
  };

  return (
    <>
      <SnackbarContext.Provider value={{ copyToClipboard, showError }}>
        {children}
      </SnackbarContext.Provider>
      <CopyToClipboardToast
        open={openClipboardSnackBar}
        onClose={handleClipboardSnackbarClose}
      />
      <DisplayToastError
        errorMessage={toastErrorState.message}
        isOpen={toastErrorState.open}
        handleClose={handleErrorAlertClose}
      />
      <DisplayErrorDialog
        isDialogOpen={dialogErrorOpen}
        handleDialogClose={handleDialogClose}
        actionType={actionType}
      />
    </>
  );
};
export default SnackbarContextWrapper;
